import type { PortfolioCoordinates, PortfolioPowerRequest } from "./assetResolution";

export type PortfolioRequestLocation = { coordinates: PortfolioCoordinates; assetIds: string[] };
export type PortfolioRequestGroup = { parameter: string; locations: PortfolioRequestLocation[] };

function coordinateKey({ lat, lon }: PortfolioCoordinates) {
  return `${lat},${lon}`;
}

/** Keep parameters apart and request colocated assets only once, retaining their identities. */
export function groupPortfolioRequestLocations(requests: readonly PortfolioPowerRequest[]): PortfolioRequestGroup[] {
  const groups = new Map<string, Map<string, PortfolioRequestLocation>>();
  for (const request of requests) {
    let locations = groups.get(request.parameter);
    if (!locations) {
      locations = new Map();
      groups.set(request.parameter, locations);
    }
    const key = coordinateKey(request.coordinates);
    const location = locations.get(key);
    if (location) {
      location.assetIds.push(request.assetId);
    } else {
      locations.set(key, { coordinates: request.coordinates, assetIds: [request.assetId] });
    }
  }
  return [...groups]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([parameter, locations]) => ({ parameter, locations: [...locations.values()] }));
}

const coordinateTolerance = 0.00001;

/** Index one parameter's response; rounded coordinates must have exactly one candidate. */
export function createPortfolioPointLookup<T extends PortfolioCoordinates>(points: readonly T[]) {
  const exact = new Map<string, T[]>();
  const buckets = new Map<string, T[]>();
  const bucket = (value: number) => Math.floor(value / coordinateTolerance);
  for (const point of points) {
    const key = coordinateKey(point);
    const matches = exact.get(key) ?? [];
    matches.push(point);
    exact.set(key, matches);
    const bucketKey = `${bucket(point.lat)},${bucket(point.lon)}`;
    const nearby = buckets.get(bucketKey) ?? [];
    nearby.push(point);
    buckets.set(bucketKey, nearby);
  }
  return (coordinates: PortfolioCoordinates): T | undefined => {
    const matches = exact.get(coordinateKey(coordinates));
    if (matches) {
      return matches.length === 1 ? matches[0] : undefined;
    }
    const candidates: T[] = [];
    for (let lat = -1; lat <= 1; lat++) {
      for (let lon = -1; lon <= 1; lon++) {
        const nearby = buckets.get(`${bucket(coordinates.lat) + lat},${bucket(coordinates.lon) + lon}`) ?? [];
        for (const point of nearby) {
          if (
            Math.abs(point.lat - coordinates.lat) <= coordinateTolerance &&
            Math.abs(point.lon - coordinates.lon) <= coordinateTolerance
          ) {
            candidates.push(point);
          }
        }
      }
    }
    return candidates.length === 1 ? candidates[0] : undefined;
  };
}
